import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  inject,
  signal,
  computed
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TagModule } from 'primeng/tag';
import { MessageService, ConfirmationService } from 'primeng/api';
import { mensajeCamposInvalidos } from 'src/app/common/mensajes-validacion';
import {
  ContactoPersona,
  ContactoPersonaRequest,
  createEmptyContactoRequest
} from 'src/app/domain/model/persona/contacto-persona';
import { ContactoPersonaRepository } from 'src/app/domain/service/persona/contacto-persona-repository';
import { NomenclaturaRepository } from 'src/app/domain/service/nomenclatura/nomenclatura-repository';
import { ContactosCursorPager } from 'src/app/presentation/shared/contactos/contactos-cursor-pager';
import { contactosPersonaPreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';
import { environment } from 'src/environments/environment';
import {
  DEFAULT_ROWS_PER_PAGE,
  PAGE_SIZE_OPTIONS
} from 'src/app/common/constants-ejb';
import {
  sanitizeText,
  sanitizeTextPreserveSpaces,
  sanitizeEmailContacto,
  emailKeypress,
  CONTACT_MAX_LENGTHS,
  isValidEmailContactoLegacy
} from 'src/app/common/input-utils';
import { BusquedaPaginadorComponent } from 'src/app/presentation/shared/busquedas/busqueda-paginador.component';

/**
 * Componente para gestión de contactos de una persona jurídica
 * Se usa dentro del diálogo de edición de personas (solo para NIT)
 *
 * @author Emerson Rodriguez - Ingeneo
 */
@Component({
  selector: 'app-contactos-persona',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    DropdownModule,
    TooltipModule,
    ConfirmDialogModule,
    TagModule,
    BusquedaPaginadorComponent
  ],
  styleUrls: ['../shared/styles/grid-table.styles.scss'],
  template: `
    <div class="contactos-container">
      <!-- Toolbar -->
      <div
        class="flex flex-col sm:flex-row justify-between sm:items-center gap-2 mb-3"
      >
        <span class="text-sm text-gray-600">
          {{ totalRegistros() }} contactos registrados
        </span>
        <button
          pButton
          label="Nuevo Contacto"
          icon="pi pi-plus"
          class="p-button-sm"
          [disabled]="!cdpersona"
          (click)="openNew()"
        ></button>
      </div>

      <!-- Tabla de contactos (igual al legacy) -->
      <div class="md:hidden space-y-3 p-3">
        @if (paginatedContactos().length === 0) {
          <div class="text-center p-4 text-gray-500">
            No se recuperó información con los parámetros ingresados.
          </div>
        }
        @for (
          contacto of paginatedContactos();
          track contacto.nmconsecutivo ?? $index
        ) {
          <div
            class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
          >
            <div class="mb-3 min-w-0">
              <div class="text-xs text-gray-500 dark:text-gray-400">Nombre</div>
              <div
                class="font-semibold text-gray-800 dark:text-gray-100 truncate"
              >
                {{ contacto.dsnombre }}
              </div>
            </div>
            <div class="grid grid-cols-1 gap-2 text-sm mb-3">
              <div>
                <span class="text-gray-500 dark:text-gray-400"
                  >{{
                    tipoPersona === 'JURIDICA' ? 'Cargo' : 'Rol/Relación'
                  }}:</span
                >
                <span class="text-gray-800 dark:text-gray-200 ml-1">
                  {{
                    tipoPersona === 'JURIDICA'
                      ? contacto.dscargo_desc ||
                        contacto.dscargo ||
                        getCargoLabel(contacto.cdcargo) ||
                        '-'
                      : contacto.dsrol || getRolLabel(contacto.cdrol) || '-'
                  }}
                </span>
              </div>
              <div>
                <span class="text-gray-500 dark:text-gray-400">Teléfono:</span>
                <span class="text-gray-800 dark:text-gray-200 ml-1">{{
                  contacto.dstelefono || '-'
                }}</span>
              </div>
              <div>
                <span class="text-gray-500 dark:text-gray-400">Email:</span>
                <span class="text-gray-800 dark:text-gray-200 ml-1">{{
                  contacto.dsemail || '-'
                }}</span>
              </div>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button
                pButton
                icon="pi pi-pencil"
                label="Modificar"
                class="p-button-outlined p-button-sm min-h-[44px]"
                (click)="editContacto(contacto)"
              ></button>
              <button
                pButton
                icon="pi pi-trash"
                label="Eliminar"
                class="p-button-danger p-button-outlined p-button-sm min-h-[44px]"
                [disabled]="contacto.snactivo === 'N'"
                (click)="confirmDelete(contacto)"
              ></button>
            </div>
          </div>
        }
      </div>

      <div class="hidden md:block">
        <div
          role="region"
          [attr.aria-label]="tableConfig().a11y.tableAriaLabel"
          class="sr-only"
        ></div>
        <p-table
          [resizableColumns]="true"
          columnResizeMode="expand"
          [value]="paginatedContactos()"
          [loading]="loading()"
          [paginator]="false"
          [scrollable]="true"
          [responsiveLayout]="'scroll'"
          [tableStyle]="{
            'min-width': tableConfig().responsive.minWidth || '72rem'
          }"
          [scrollHeight]="tableConfig().responsive.scrollHeight || '400px'"
          [styleClass]="tableStyleClass()"
        >
          <ng-template pTemplate="header">
            <tr>
              <th pResizableColumn>
                <span class="grid-header-text" title="Nombre">Nombre</span>
              </th>
              <th pResizableColumn style="width: 150px">
                <span
                  class="grid-header-text"
                  [attr.title]="
                    tipoPersona === 'JURIDICA' ? 'Cargo' : 'Rol/Relación'
                  "
                  >{{
                    tipoPersona === 'JURIDICA' ? 'Cargo' : 'Rol/Relación'
                  }}</span
                >
              </th>
              <th pResizableColumn style="width: 120px">
                <span class="grid-header-text" title="Teléfono">Teléfono</span>
              </th>
              <th pResizableColumn style="width: 200px">
                <span class="grid-header-text" title="Email">Email</span>
              </th>
              <th pResizableColumn style="width: 80px" class="text-center">
                <span class="grid-header-text" title="Acciones">Acciones</span>
              </th>
            </tr>
          </ng-template>

          <ng-template pTemplate="body" let-contacto>
            <tr>
              <td>
                <span
                  class="grid-cell-text"
                  [attr.title]="textoTabla(contacto.dsnombre)"
                  >{{ textoTabla(contacto.dsnombre) }}</span
                >
              </td>
              <td>
                @if (tipoPersona === 'JURIDICA') {
                  <span
                    class="grid-cell-text"
                    [attr.title]="
                      textoTabla(
                        contacto.dscargo_desc ||
                          contacto.dscargo ||
                          getCargoLabel(contacto.cdcargo)
                      )
                    "
                    >{{
                      textoTabla(
                        contacto.dscargo_desc ||
                          contacto.dscargo ||
                          getCargoLabel(contacto.cdcargo)
                      )
                    }}</span
                  >
                } @else {
                  <span
                    class="grid-cell-text"
                    [attr.title]="
                      textoTabla(contacto.dsrol || getRolLabel(contacto.cdrol))
                    "
                    >{{
                      textoTabla(contacto.dsrol || getRolLabel(contacto.cdrol))
                    }}</span
                  >
                }
              </td>
              <td class="font-mono text-sm">
                <span
                  class="grid-cell-text"
                  [attr.title]="textoTabla(contacto.dstelefono)"
                  >{{ textoTabla(contacto.dstelefono) }}</span
                >
              </td>
              <td>
                <span
                  class="grid-cell-text"
                  [pTooltip]="contacto.dsemail"
                  [attr.title]="textoTabla(contacto.dsemail)"
                  >{{ textoTabla(contacto.dsemail) }}</span
                >
              </td>
              <td class="text-center" style="white-space: nowrap;">
                <button
                  pButton
                  icon="pi pi-pencil"
                  class="p-button-rounded p-button-text p-button-sm min-w-[44px] min-h-[44px]"
                  pTooltip="Modificar"
                  (click)="editContacto(contacto)"
                ></button>
                <button
                  pButton
                  icon="pi pi-trash"
                  class="p-button-rounded p-button-text p-button-danger p-button-sm min-w-[44px] min-h-[44px]"
                  pTooltip="Eliminar"
                  [disabled]="contacto.snactivo === 'N'"
                  (click)="confirmDelete(contacto)"
                ></button>
              </td>
            </tr>
          </ng-template>

          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="5" class="text-center p-4 text-gray-500">
                <i class="pi pi-users text-3xl mb-2 block"></i>
                No se recuperó información con los parámetros ingresados.
              </td>
            </tr>
          </ng-template>
        </p-table>
      </div>

      <!-- Paginador manual -->
      @if (totalRegistros() > 0) {
        <div class="text-sm text-gray-500 px-3 pt-2">
          Registros del {{ registroInicio() }} hasta el {{ registroFin() }} de
          {{ totalRegistros() }}
        </div>
        <app-busqueda-paginador
          [currentPage]="currentPage()"
          [totalPages]="totalPages()"
          [hasPrevious]="hasPrevious()"
          [hasNext]="hasNext()"
          [pageSizeOptions]="pageSizeOptions"
          [selectedPageSize]="selectedPageSize"
          (pageSizeChange)="selectedPageSize = $event; onPageSizeChange()"
          (first)="irPrimeraPagina()"
          (previous)="irPaginaAnterior()"
          (next)="irPaginaSiguiente()"
          (last)="irUltimaPagina()"
        ></app-busqueda-paginador>
      }

      <!-- Dialog Crear/Editar Contacto -->
      <p-dialog
        [(visible)]="dialogVisible"
        [header]="editMode() ? 'Editar Contacto' : 'Nuevo Contacto'"
        [modal]="true"
        [style]="{ width: '92vw', maxWidth: '32rem' }"
        [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
        [breakpoints]="{ '768px': '94vw', '640px': '96vw' }"
        [closable]="true"
        [draggable]="false"
        appendTo="body"
      >
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <!-- Nombre -->
          <div class="field col-span-2">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Nombre Completo *
            </label>
            <input
              pInputText
              [(ngModel)]="contactoForm.dsnombre"
              (change)="
                contactoForm.dsnombre = sanitizeTextPreserveSpaces(
                  contactoForm.dsnombre
                )
              "
              placeholder="Nombre del contacto..."
              [maxlength]="maxLengths.NOMBRE"
              class="w-full"
            />
          </div>

          <!-- Cargo (solo para Persona Jurídica) -->
          @if (tipoPersona === 'JURIDICA') {
            <div class="field col-span-2">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Cargo
              </label>
              <p-dropdown
                [(ngModel)]="contactoForm.cdcargo"
                [options]="cargos"
                optionLabel="descripcion"
                optionValue="codigo"
                placeholder="Seleccione..."
                appendTo="body"
                [showClear]="true"
                [filter]="true"
                filterBy="descripcion"
                [style]="{ width: '100%' }"
              >
              </p-dropdown>
            </div>
          }

          <!-- Rol/Relación (solo para Persona Natural) -->
          @if (tipoPersona === 'NATURAL') {
            <div class="field col-span-2">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Rol/Relación
              </label>
              <p-dropdown
                [(ngModel)]="contactoForm.cdrol"
                [options]="roles"
                optionLabel="descripcion"
                optionValue="codigo"
                placeholder="Seleccione..."
                appendTo="body"
                [showClear]="true"
                [filter]="true"
                filterBy="descripcion"
                [style]="{ width: '100%' }"
              >
              </p-dropdown>
            </div>
          }

          <!-- Teléfono -->
          <div class="field">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Teléfono
            </label>
            <input
              pInputText
              [(ngModel)]="contactoForm.dstelefono"
              (change)="
                contactoForm.dstelefono = sanitizeTextPreserveSpaces(
                  contactoForm.dstelefono
                )
              "
              placeholder="Ej: 604-1234567"
              [maxlength]="maxLengths.TELEFONO"
              class="w-full"
            />
          </div>

          <!-- Email -->
          <div class="field">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              pInputText
              type="email"
              [(ngModel)]="contactoForm.dsemail"
              (keypress)="emailKeypress($event)"
              (change)="
                contactoForm.dsemail = sanitizeEmailContacto(contactoForm.dsemail)
              "
              placeholder="correo@empresa.com"
              [maxlength]="maxLengths.EMAIL"
              class="w-full"
            />
          </div>
        </div>

        <ng-template pTemplate="footer">
          <div
            class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
          >
            <p-button
              label="Cerrar"
              icon="pi pi-times"
              [outlined]="true"
              (onClick)="hideDialog()"
            >
            </p-button>
            <p-button
              label="Guardar"
              icon="pi pi-check"
              [loading]="saving()"
              (onClick)="saveContacto()"
            >
            </p-button>
          </div>
        </ng-template>
      </p-dialog>

      <!-- Confirm Dialog para eliminar -->
      <p-confirmDialog key="contactoConfirm"></p-confirmDialog>
    </div>
  `
})
export class ContactosPersonaComponent implements OnInit, OnChanges {
  @Input() cdpersona?: number;
  @Input() tipoPersona: 'JURIDICA' | 'NATURAL' = 'NATURAL'; // Para mostrar Cargo o Rol según tipo
  @Input() visible = false; // Para forzar recarga cuando el diálogo padre se abre

  // Inyectando PUERTOS (no adaptadores) - Arquitectura Hexagonal
  private readonly contactoRepository = inject(ContactoPersonaRepository);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly nomenclaturaService = inject(NomenclaturaRepository);

  // Paginación REAL en servidor (cursor/keyset): solo la página actual vive en memoria
  private readonly pager = new ContactosCursorPager((cursor, size, direction) =>
    // Solo traer contactos activos (igual que legacy)
    this.contactoRepository.getByPersonaPaged(
      this.cdpersona!,
      true,
      cursor,
      size,
      direction
    )
  );

  // Estado
  readonly contactos = this.pager.items;
  loading = signal(false);
  saving = signal(false);
  editMode = signal(false);
  catalogosCargados = signal(false);
  dialogVisible = false;
  private cargandoContactos = false;

  // Formulario
  contactoForm: ContactoPersonaRequest = createEmptyContactoRequest();
  editingId?: number;

  // Catálogos (cargados desde API)
  roles: { codigo: number | string; descripcion: string }[] = [];
  cargos: { codigo: number | string; descripcion: string }[] = [];

  // Paginación (el estado vive en el paginador de servidor)
  readonly currentPage = this.pager.currentPage;
  readonly pageSize = this.pager.pageSize;
  pageSizeOptions = PAGE_SIZE_OPTIONS;
  selectedPageSize = DEFAULT_ROWS_PER_PAGE;

  readonly tableConfig = computed(() =>
    contactosPersonaPreset.createConfig({
      data: this.paginatedContactos(),
      style: {
        tableClassName: 'tabla-cambio-orientacion',
        striped: true,
        gridlines: false,
        resizableColumns: true,
        rowHover: false
      },
      state: {
        loading: this.loading(),
        emptyMessage: 'No se recuperó información con los parámetros ingresados.'
      },
      responsive: {
        mode: 'hybrid',
        minWidth: '72rem',
        scrollHeight: '400px',
        cardBreakpointPx: 768
      },
      a11y: {
        tableAriaLabel: 'Tabla de contactos de persona',
        announceSelection: false,
        announcePageChanges: true,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly tableStyleClass = computed(() => {
    const config = this.tableConfig();
    const classes = ['p-datatable-sm', config.style.tableClassName];
    if (config.style.gridlines !== false) {
      classes.push('p-datatable-gridlines');
    }
    if (config.style.striped !== false) {
      classes.push('p-datatable-striped');
    }
    return classes.join(' ');
  });

  // Sanitización XSS
  sanitizeText = sanitizeText;
  sanitizeTextPreserveSpaces = sanitizeTextPreserveSpaces;
  sanitizeEmailContacto = sanitizeEmailContacto;
  readonly maxLengths = CONTACT_MAX_LENGTHS;
  emailKeypress = emailKeypress;

  readonly totalRegistros = this.pager.totalRecords;
  readonly totalPages = this.pager.totalPages;
  readonly paginatedContactos = this.pager.items;
  readonly hasPrevious = this.pager.hasPrevious;
  readonly hasNext = this.pager.hasNext;
  readonly registroInicio = this.pager.registroInicio;
  readonly registroFin = this.pager.registroFin;

  ngOnInit(): void {
    this.cargarCatalogos();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const recargarPorPersona = changes['cdpersona'] && this.cdpersona;
    const recargarPorVisibilidad =
      changes['visible'] &&
      changes['visible'].currentValue === true &&
      this.cdpersona;

    // Recargar cuando cambia persona o al abrir el diálogo con persona ya seleccionada.
    if (recargarPorPersona || recargarPorVisibilidad) {
      this.cargarContactos();
    }
  }

  irPrimeraPagina(): void {
    void this.navegar(() => this.pager.cargarPrimera());
  }
  irUltimaPagina(): void {
    void this.navegar(() => this.pager.ultima());
  }
  irPaginaAnterior(): void {
    void this.navegar(() => this.pager.anterior());
  }
  irPaginaSiguiente(): void {
    void this.navegar(() => this.pager.siguiente());
  }
  onPageSizeChange(): void {
    void this.navegar(() => this.pager.cambiarTamano(this.selectedPageSize));
  }

  /** Cada cambio de página es una consulta al backend con el cursor de la página actual. */
  private async navegar(accion: () => Promise<void>): Promise<void> {
    if (!this.cdpersona) return;
    try {
      await accion();
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudieron cargar los contactos'
      });
    }
  }

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  /**
   * Carga roles y cargos desde el backend
   * Equivale a: UtilidadesBuscadores.llenarListBox() del legacy
   */
  async cargarCatalogos(): Promise<void> {
    try {
      const [rolesData, cargosData] = await Promise.all([
        this.nomenclaturaService.listarRoles(),
        this.nomenclaturaService.listarCargos()
      ]);
      // Convertir codigo a number para coincidir con cdrol/cdcargo del modelo
      this.roles = rolesData.map((r) => ({
        codigo: parseInt(r.codigo, 10) || r.codigo,
        descripcion: r.descripcion
      }));
      this.cargos = cargosData.map((c) => ({
        codigo: parseInt(c.codigo, 10) || c.codigo,
        descripcion: c.descripcion
      }));
      this.catalogosCargados.set(true);
    } catch (error) {
      this.catalogosCargados.set(true); // Marcar como cargados aunque haya error
    }
  }

  /** Carga la primera página (al abrir o al cambiar de persona). */
  async cargarContactos(): Promise<void> {
    return this.consultarPagina(() => this.pager.cargarPrimera());
  }

  /** Repite la consulta de la página actual (tras guardar o inactivar un contacto). */
  private async recargarPaginaActual(): Promise<void> {
    return this.consultarPagina(() => this.pager.recargar());
  }

  private async consultarPagina(accion: () => Promise<void>): Promise<void> {
    if (!this.cdpersona || this.cargandoContactos) return;

    this.cargandoContactos = true;
    this.loading.set(true);
    try {
      await accion();
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudieron cargar los contactos'
      });
    } finally {
      this.cargandoContactos = false;
      this.loading.set(false);
    }
  }

  getRolLabel(codigo?: number | string): string {
    if (codigo === undefined || codigo === null) return '-';
    const codigoStr = String(codigo);
    const rol = this.roles.find((r) => String(r.codigo) === codigoStr);
    return rol?.descripcion || `Rol ${codigo}`;
  }

  getCargoLabel(codigo?: number | string): string {
    if (codigo === undefined || codigo === null) return '-';
    const codigoStr = String(codigo);
    const cargo = this.cargos.find((c) => String(c.codigo) === codigoStr);
    return cargo?.descripcion || `Cargo ${codigo}`;
  }

  openNew(): void {
    this.contactoForm = createEmptyContactoRequest();
    if (this.cdpersona) {
      this.contactoForm.cdpersona = this.cdpersona;
    }
    this.editingId = undefined;
    this.editMode.set(false);
    this.dialogVisible = true;
  }

  async editContacto(contacto: ContactoPersona): Promise<void> {
    // Esperar a que los catálogos están cargados
    if (!this.catalogosCargados()) {
      await this.cargarCatalogos();
    }

    // Asegurar que cdcargo/cdrol sean number para coincidir con el dropdown
    const cdcargoValue =
      contacto.cdcargo !== null && contacto.cdcargo !== undefined
        ? Number(contacto.cdcargo)
        : undefined;
    const cdrolValue =
      contacto.cdrol !== null && contacto.cdrol !== undefined
        ? Number(contacto.cdrol)
        : undefined;

    // Primero abrir el diálogo y luego asignar valores
    this.editingId = contacto.nmconsecutivo;
    this.editMode.set(true);
    this.dialogVisible = true;

    // Usar setTimeout para asegurar que el diálogo está renderizado
    setTimeout(() => {
      this.contactoForm = {
        cdpersona: contacto.cdpersona || this.cdpersona!,
        cdrol: cdrolValue,
        cdcargo: cdcargoValue,
        dsnombre: contacto.dsnombre || '',
        dscargo: contacto.dscargo,
        dstelefono: contacto.dstelefono,
        dscelular: contacto.dscelular,
        dsemail: contacto.dsemail
      };
    }, 0);
  }

  hideDialog(): void {
    this.dialogVisible = false;
    this.contactoForm = createEmptyContactoRequest();
    this.editingId = undefined;
  }

  async saveContacto(): Promise<void> {
    // Validaciones básicas
    if (!this.contactoForm.dsnombre?.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: mensajeCamposInvalidos(['Nombre'])
      });
      return;
    }

    // Al escribir se preservan los espacios (sanitizeTextPreserveSpaces); la limpieza
    // final con trim se aplica una sola vez, al guardar.
    // Legacy (AdminContactosViewImpl): el nombre se normaliza a mayúsculas (toUpperCase)
    this.contactoForm.dsnombre = sanitizeText(this.contactoForm.dsnombre).toUpperCase();
    // Legacy: teléfono de texto libre (sin regex); solo se recorta
    if (this.contactoForm.dstelefono != null) {
      this.contactoForm.dstelefono = sanitizeText(this.contactoForm.dstelefono);
    }

    // Legacy: widgetEmail.setRegex(Constants.REGEX_EMAILS); el email es opcional
    const email = this.contactoForm.dsemail?.trim();
    if (email && !isValidEmailContactoLegacy(email)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: mensajeCamposInvalidos(['Email'])
      });
      return;
    }

    // El email es opcional: un campo vaciado se envía ausente (el backend rechaza "" por @Pattern)
    if (email) {
      this.contactoForm.dsemail = email;
    } else {
      delete this.contactoForm.dsemail;
    }

    this.saving.set(true);
    try {
      if (this.editMode() && this.editingId) {
        await this.contactoRepository.update(
          this.cdpersona!,
          this.editingId,
          this.contactoForm
        );
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: 'Los datos se actualizaron correctamente.'
        });
      } else {
        await this.contactoRepository.create(
          this.cdpersona!,
          this.contactoForm
        );
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: 'Los datos se almacenaron correctamente.'
        });
      }

      this.hideDialog();
      await this.recargarPaginaActual();
    } catch (error: any) {
      const detail =
        error?.error?.detail ||
        error?.error?.message ||
        'No se pudo guardar el contacto';
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail
      });
    } finally {
      this.saving.set(false);
    }
  }

  confirmDelete(contacto: ContactoPersona): void {
    this.confirmationService.confirm({
      key: 'contactoConfirm',
      message: '¿Esta seguro que desea eliminar el registro seleccionado?',
      header: 'Confirmar Inactivación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, Inactivar',
      rejectLabel: 'Cancelar',
      accept: () => this.deleteContacto(contacto)
    });
  }

  async deleteContacto(contacto: ContactoPersona): Promise<void> {
    if (!contacto.nmconsecutivo) return;

    this.loading.set(true);
    try {
      await this.contactoRepository.delete(
        this.cdpersona!,
        contacto.nmconsecutivo
      );
      this.messageService.add({
        severity: 'success',
        summary: 'Éxito',
        detail: 'Los datos se eliminaron correctamente.'
      });
      await this.recargarPaginaActual();
    } catch (error: any) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail:
          error?.error?.detail ||
          error?.error?.message ||
          'No se pudo inactivar el contacto'
      });
    } finally {
      this.loading.set(false);
    }
  }
}
